// home.jsx — Home page (the living map / hero + discovery).
const { useState: useStateH, useEffect: useEffectH } = React;

function ProgressRing({ value, size = 30 }) {
  const r = (size - 4) / 2, c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border-2)" strokeWidth="2.5" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--accent)" strokeWidth="2.5"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - value)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: "stroke-dashoffset 1s var(--ease)" }} />
    </svg>
  );
}

// "סעיף ד" for a card whose last position is a seif inside a letter.
function continueSectionLabel(card) {
  const match = String((card && card.lastSegmentRef) || "").match(/:seif:(\d+)/);
  return match && window.heNumeral ? `סעיף ${window.heNumeral(match[1])}` : "";
}

function Home({ onOpenSicha, onOpenTopic, onOpenExplore, onOpenResearch, onOpenLibrary, onOpenRef, direction = "beis", onDirection }) {
  const {topicsVisible} = window.useSiteSettings();
  const [hoverNode, setHoverNode] = useStateH(null);
  const weekly = window.WEEKLY_FEATURE || {};
  const graphState = window.useFullSichaPreview(weekly.ref || window.SICHA?._liveTargetRef || window.SICHA?._liveRef);
  const sources = graphState.sources;
  const featured = weekly.ref ? { titleHe: weekly.heTitle, titleEn: weekly.title, parsha: weekly.caption || weekly.parsha } : (window.SICHA || {});
  const hovered = hoverNode ? sources.find((s) => `src-${s.n}` === hoverNode || s.id === hoverNode) : null;
  const heroRef = window.useStagger({ y: 20, step: 110, dur: 760 });
  const graphRef = window.useEntrance({ x: 46, s: 0.95, blur: 6, dur: 900, delay: 240 });
  const revealRef = window.useScrollReveal();
  const openFeatured = () => weekly.ref && onOpenRef ? onOpenRef(weekly.ref) : onOpenSicha();
  const openFeaturedExplore = () => {
    if (!weekly.ref || !window.loadSicha) { onOpenExplore(); return; }
    window.loadSicha(weekly.ref, { recordActivity: false }).then((loaded) => { if (loaded) onOpenExplore(); });
  };

  return (
    <div className="home" ref={revealRef} style={{ background: "var(--hero-grad)" }}>
      {/* HERO */}
      <section className="hero">
        <div className="hero-left" ref={heroRef}>
          <div className="hero-topbar">
            <div className="hero-brandlock">
              <span>
                <b>Mayanos <em lang="he" dir="rtl" className="heb">מעיינות</em></b>
                <small>A living library of Chassidus</small>
              </span>
            </div>
          </div>
          <div className="home-sponsorship" role="note">
            <span>{window.MAYANOS_SPONSORSHIP?.label || "Today's learning is sponsored"}</span>
            <strong lang="he" dir="rtl" className="heb">{window.MAYANOS_SPONSORSHIP?.compact || "לע״נ יעקב קלמן בן אלטר יהושע הכהן"}</strong>
          </div>
          <h1 className="hero-title serif">Learn the text.<br /><em>Follow its sources.</em></h1>
          <p className="hero-sub">Read Chassidus in Hebrew and English, explore the sources, and collect passages for your own learning.</p>

          <div className="hero-proof" aria-label="Library features">
            <span><Icon d="graph" size={15} /><b>Source-linked</b><small>Follow the chain</small></span>
            <span><Icon d="text" size={15} /><b>Bilingual</b><small>Where available</small></span>
            <span><Icon d="book" size={15} /><b>Whole library</b><small>Chassidus and beyond</small></span>
          </div>

          <button className="btn btn-primary hero-library-cta library-entry-cta" onClick={onOpenLibrary}>
            <Icon d="book" size={21} /><b>Open the library</b>
            <span className="library-entry-arrow"><Icon d="chev" size={19} /></span>
          </button>

          <div className="hero-actions">
            <button className="btn" onClick={openFeatured}><Icon d="text" size={16} /> {weekly.ref ? (weekly.kind === "parsha" ? "Open this week’s sicha" : "Open today’s sicha") : "Open a featured text"}</button>
            {topicsVisible && <button className="btn" onClick={onOpenTopic ? () => onOpenTopic("") : undefined}><Icon d="compass" size={16} /> Explore a topic</button>}
            <button className="btn" onClick={onOpenResearch}><Icon d="layers" size={16} /> Create a source sheet</button>
          </div>
          {window.CORPUS_STATS && <p>{window.CORPUS_STATS.translationPercent}% of the Mayanos corpus has English text. Coverage varies by work.</p>}

        </div>

        {/* live source graph */}
        <div className="hero-graph" ref={graphRef}>
          <div className="hero-graph-card">
            <div className="hgc-head">
              <span lang="he" dir="rtl" className="heb hgc-he">מפת מקורות</span>
              <button className="hgc-tag hgc-link" onClick={openFeaturedExplore}><Icon d="graph" size={12} /> Open full map <Icon d="chev" size={11} /></button>
            </div>
            {weekly.ref && <div className="hgc-weekly"><span>{weekly.kind === "parsha" ? "This week’s sicha" : "Today’s sicha"}</span><b>{weekly.title}</b><em>{[weekly.caption || weekly.parsha, weekly.sichaLabel].filter(Boolean).join(" · ")}</em></div>}
            <div className="hgc-scope-status" role="status">{graphState.loading ? "Loading full-text connections…" : graphState.error ? "Could not load the full map. Open the map to retry." : `${sources.length} sources across ${graphState.count} sections`}</div>
            <SourceGraph center={{collection: weekly.title || "Featured text"}} sources={sources} height={330} active={hoverNode}
              onHover={setHoverNode} animate />
            <div className="hgc-foot">
              {hovered ? (
                <div className="hgc-preview" key={hovered.id}>
                  <div><TypeDot type={hovered.type} /> <b>{hovered.title}</b> {hovered.citation}</div>
                  <span lang="he" dir="rtl" className="heb hgc-prev-he">{hovered.he}</span>
                </div>
              ) : (
                <div className="hgc-idle">Select a numbered source to inspect the sources connected to {weekly.ref ? "this week’s sicha" : "this featured text"}.</div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="home-pathways hsection" aria-labelledby="pathways-title">
        <div className="hsec-head home-pathways-head">
          <div>
            <span className="home-section-kicker">Choose your way in</span>
            <h2 id="pathways-title">Learn, trace, or build</h2>
          </div>
          <p>Begin with a text, follow its Torah sources, or gather everything into a shiur.</p>
        </div>
        <div className="home-pathway-grid">
          <button className="home-pathway" data-reveal onClick={onOpenLibrary}>
            <span className="home-pathway-num">01</span>
            <span className="home-pathway-icon"><Icon d="book" size={21} /></span>
            <strong>Learn a text</strong>
            <small>Browse Likkutei Sichos, maamarim, Tanya, and the full library.</small>
            <em>Open the library <Icon d="chev" size={13} /></em>
          </button>
          <button className="home-pathway" data-reveal data-reveal-delay="70" onClick={onOpenExplore}>
            <span className="home-pathway-num">02</span>
            <span className="home-pathway-icon"><Icon d="graph" size={21} /></span>
            <strong>Trace the sources</strong>
            <small>See how a teaching connects to Tanach, Talmud, Midrash, and Chassidus.</small>
            <em>Explore connections <Icon d="chev" size={13} /></em>
          </button>
          <button className="home-pathway" data-reveal data-reveal-delay="140" onClick={onOpenResearch}>
            <span className="home-pathway-num">03</span>
            <span className="home-pathway-icon"><Icon d="layers" size={21} /></span>
            <strong>Build a shiur</strong>
            <small>Collect passages and sources into a clear, shareable teaching outline.</small>
            <em>Open the workspace <Icon d="chev" size={13} /></em>
          </button>
        </div>
      </section>

      {/* TODAY — deliberately placed immediately before Continue learning. */}
      <section className="hsection home-daily" aria-labelledby="today-title">
        <div className="home-daily-copy">
          <div className="today-tag"><span className="dot" style={{ background: "var(--gold)" }} /> Today in Mayanos</div>
          <div lang="he" dir="rtl" className="today-date heb">{window.TODAY.hayomyom.date}</div>
          <h2 id="today-title">Hayom Yom{window.TODAY.parsha ? ` · ${window.TODAY.parsha}` : ""}</h2>
          <button className="btn" disabled={!window.TODAY.hayomyom.ref} onClick={() => onOpenRef && onOpenRef(window.TODAY.hayomyom.ref)}>Open today’s Hayom Yom</button>
        </div>
        <div className="home-daily-text">
          {window.TODAY.hayomyom.he ? <p lang="he" dir="rtl" className="today-he heb">{window.TODAY.hayomyom.he}</p> : <p className="today-en">Today’s entry is not available.</p>}
          {window.TODAY.hayomyom.en && <p className="today-en serif">{window.TODAY.hayomyom.en}</p>}
          {window.TODAY.attribution && <a className="today-credit" href={window.TODAY.attribution.url} target="_blank" rel="noreferrer">Hebrew date by Hebcal</a>}
        </div>
      </section>

      {/* CONTINUE — shown only when this browser has real reading history */}
      {window.CONTINUE.length > 0 && <section className="hsection">
        <div className="hsec-head"><h2>Continue reading</h2></div>
        <div className="cont-row">
          {window.CONTINUE.map((c, i) => (
            <button key={c.id} className="cont-card" data-reveal data-reveal-delay={i * 80} onClick={() => onOpenRef && onOpenRef(c.lastSegmentRef || c.ref)}>
              <ProgressRing value={c.progress} />
              <div className="cont-txt">
                <span lang="he" dir="rtl" className="heb cont-he">{c.he}</span>
                <span className="cont-en">{c.en}</span>
                <i>{[c.coll, continueSectionLabel(c)].filter(Boolean).join(" · ")}</i>
              </div>
              <span className="cont-pct">{Math.round(c.progress * 100)}%</span>
            </button>
          ))}
        </div>
      </section>}

      {/* COLLECTIONS + TODAY */}
      <section className="hsection two-col">
        <div>
          <div className="hsec-head"><h2>Featured collections</h2><button className="link-btn" onClick={onOpenLibrary}>Library <Icon d="chev" size={14} /></button></div>
          <div className="coll-grid">
            {window.COLLECTIONS.map((c, i) => (
              <button key={c.id} className="coll-card" data-reveal data-reveal-delay={i * 55} onClick={() => onOpenRef && onOpenRef(c.ref || c.en)}>
                <div className="coll-top">
                  <span lang="he" dir="rtl" className="heb coll-he">{c.he}</span>
                  {Number.isFinite(c.density) && <span className="coll-density" title={`Graph density ${Math.round(c.density * 100)}%`}>
                    <ProgressRing value={c.density} size={22} />
                  </span>}
                </div>
                <div className="coll-en">{c.en}</div>
                <div className="coll-author">{c.author}</div>
                <p className="coll-desc">{c.desc}</p>
                {(c.vols || c.sources) && <div className="coll-meta">
                  {c.vols && <span><Icon d="book" size={12} /> {c.vols} vol</span>}
                  {c.sources && <span><Icon d="graph" size={12} /> {c.sources} sources</span>}
                </div>}
              </button>
            ))}
          </div>
        </div>

        <aside className="today">
          {topicsVisible && <div className="topics-card" data-reveal data-reveal-delay="90">
            <div className="today-tag"><Icon d="compass" size={13} /> Explore by concept</div>
            <div className="topics-cloud">
              {window.TOPICS.map((t) => {
                const slug = t.slug || t.id;
                return (
                  <button key={slug} className="topic-pill" onClick={() => onOpenTopic && onOpenTopic(slug)}>
                    <span lang="he" dir="rtl" className="heb">{t.he}</span>
                    <span className="topic-en">{t.en}</span>
                  </button>
                );
              })}
              {!window.TOPICS.length && <span className="rail-hint">Open Topics to search Sefaria’s live topic index.</span>}
            </div>
          </div>}
        </aside>
      </section>

      <div style={{ height: 60 }} />
    </div>
  );
}

Object.assign(window, { Home, ProgressRing });
